import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { client, MarketOrderType, Instance, Market } from '../../../app/services/api/orderManagementClient';
import { convertISODurationToTimeInput, convertTimeInputToISODuration } from '../../../utils/timeUtils';
import useLogError from '../../../hooks/useLogError';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';
import { isDemoModeEnabled } from '../../../utils/featureAccess';

/** Demo Mode only - order routing/supplier channel strategy. No backend field exists yet
 * on MarketOrderType for this; captured locally, not persisted on save. */
enum OrderRoutingStrategy {
  OdsSevona = 'OdsSevona',
  IntegratedSupplier = 'IntegratedSupplier',
  NonIntegratedCsv = 'NonIntegratedCsv',
  PendingApprovalHold = 'PendingApprovalHold',
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel"
      hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const OrderChannelForm = () => {
  const { t } = useTranslation('orderTypes');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const { logError, extractErrorMessage } = useLogError();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { instances, getMarkets, ensureMarketsLoaded } = useInstanceMarket();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);
  const [tabValue, setTabValue] = useState<number>(0);
  const isDemoMode = isDemoModeEnabled();

  // Demo Mode only - order routing/supplier channel strategy, see OrderRoutingStrategy above.
  const [routingStrategy, setRoutingStrategy] = useState<OrderRoutingStrategy>(OrderRoutingStrategy.IntegratedSupplier);
  const [csvExportEmail, setCsvExportEmail] = useState<string>('');
  const [approvalHoldReason, setApprovalHoldReason] = useState<string>('');

  const companyId = searchParams.get('companyId');
  const customerTypeId = searchParams.get('customerTypeId');
  const searchTerm = searchParams.get('searchTerm');
  
  const [formData, setFormData] = useState<MarketOrderType>({
    companyOrderTypeId: 0,
    instanceName: '',
    companyId: 0,
    orderTypeId: 0,
    name: '',
    marketName: '',
    customerTypeId: undefined,
    internal: false,
    external: false,
    orderingOnly: false,
    code: '',
    minimumOrderValue: 0,
    cutoff: {
      customerTypeOrderTypeId: undefined,
      orderCutOffTime: '',
      canPushOrderInEnteredDOS: false,
      supplierName: '',
      supplierEmailAddress: '',
      supplierEmailAddress2: '',
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const markets = getMarkets(formData.companyId ? formData.companyId.toString() : '');

  const normalizeOrderType = (data: MarketOrderType): MarketOrderType => ({
    ...data,
    name: data.name ?? '',
    instanceName: data.instanceName ?? '',
    marketName: data.marketName ?? '',
    code: data.code ?? '',
    internal: data.internal ?? false,
    external: data.external ?? false,
    orderingOnly: data.orderingOnly ?? false,
    minimumOrderValue: data.minimumOrderValue ?? 0,
    cutoff: {
      customerTypeOrderTypeId: data.cutoff?.customerTypeOrderTypeId ?? undefined,
      orderCutOffTime: data.cutoff?.orderCutOffTime ?? '',
      canPushOrderInEnteredDOS: data.cutoff?.canPushOrderInEnteredDOS ?? false,
      supplierName: data.cutoff?.supplierName ?? '',
      supplierEmailAddress: data.cutoff?.supplierEmailAddress ?? '',
      supplierEmailAddress2: data.cutoff?.supplierEmailAddress2 ?? '',
    },
  });


  const fetchOrderType = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const result = await client.getMarketOrderType(id);
      if (result.success && result.data) {
        const normalized = normalizeOrderType(result.data);
        setFormData(normalized);
        if (result.data.companyId) {
          ensureMarketsLoaded(result.data.companyId.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching order type:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchOrderType();
    }
  }, [id, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = t('nameRequired');
    }

    if (!formData.companyId) {
      newErrors.companyId = t('instanceRequired');
    }

    if (formData.minimumOrderValue !== undefined && formData.minimumOrderValue < 0) {
      newErrors.minimumOrderValue = t('minimumOrderValueMustBePositive');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof MarketOrderType) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let value: string | number = event.target.value;
    
    if (field === 'orderTypeId' || field === 'minimumOrderValue') {
      // Handle empty string to maintain the ability to clear the field
      value = value === '' ? 0 : parseInt(value) || 0;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleCutoffChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let value: string | boolean = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    
    if (field === 'orderCutOffTime' && typeof value === 'string') {
      const isoDuration = convertTimeInputToISODuration(value);
      value = isoDuration || '';
    }
    
    setFormData(prev => ({
      ...prev,
      cutoff: {
        ...prev.cutoff,
        [field]: value,
      },
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setFormData(prev => ({
      ...prev,
      companyId: value?.id || 0,
      instanceName: value?.name || '',
      customerTypeId: undefined,
      marketName: '',
    }));
    
    if (value?.id) {
      ensureMarketsLoaded(value.id.toString());
    }
    
    if (errors.companyId) {
      setErrors(prev => ({
        ...prev,
        companyId: '',
      }));
    }
  };

  const handleMarketChange = (event: React.SyntheticEvent, value: Market | null) => {
    setFormData(prev => ({
      ...prev,
      customerTypeId: value?.customerTypeId,
      marketName: value?.name || '',
    }));
  };

  const handleCheckboxChange = (field: keyof MarketOrderType) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        const result = await client.updateMarketOrderType(id!, formData);
        if (result.success) {
          setMessage(t('procurementUpdateSuccessMessage', 'Order channel updated successfully'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/procurement/order-channels${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      } else {
        const result = await client.createMarketOrderType(formData);
        if (result.success) {
          setMessage(t('procurementCreateSuccessMessage', 'Order channel created successfully'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/procurement/order-channels${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      }
    } catch (error) {
      logError(error);
      const errorMessage = extractErrorMessage(error);
      setMessage(errorMessage || (isEdit
        ? t('procurementUpdateErrorMessage', 'Failed to update order channel')
        : t('procurementCreateErrorMessage', 'Failed to create order channel')));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/procurement/order-channels${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'OrderTypeUpdate' : 'OrderTypeCreate', 'ConfigurationAdmin']);

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{t(isEdit ? 'procurementEditOrderChannel' : 'procurementCreateOrderChannel', isEdit ? 'Edit Order Channel' : 'Create Order Channel')}</PageTitle>
        <Typography>{t('loading')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack spacing={6}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 4,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <PageTitle>{t(isEdit ? 'procurementEditOrderChannel' : 'procurementCreateOrderChannel', isEdit ? 'Edit Order Channel' : 'Create Order Channel')}</PageTitle>
            <Typography
              variant="textSM"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              {t('procurementOrderChannelFormSubtitle', 'Manage order channel setup, defaults, and cutoff details.')}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={3}
            justifyContent="flex-end"
          >
            <Button
              variant="secondary"
              size="lg"
              onClick={handleCancel}
            >
              {t('cancel')}
            </Button>
            {userCanSave && (
              <Button
                variant="primary"
                size="lg"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? t('saving') : (isEdit ? t('update') : t('create'))}
              </Button>
            )}
          </Stack>
        </Box>

        <Box>
          <Tabs
            value={tabValue}
            onChange={(_event, newValue) => setTabValue(newValue)}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: '-1px',
              ml: 4,
              mr: 4,
              px: 2,
            }}
            variant="scrollable"
            scrollButtons={false}
            allowScrollButtonsMobile={false}
          >
            <Tab label={t('overview', 'Overview')} />
            <Tab label={t('ordering', 'Ordering')} />
            {isDemoMode && <Tab label={t('routing', 'Routing')} />}
          </Tabs>

          <Section style={{ mt: 0, p: 5, pb: 0 }}>
            <Box sx={{ p: { xs: 4, md: 5 } }}>
              <TabPanel value={tabValue}
                index={0}>
                <Grid container
                  spacing={4}>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('procurementOrderChannelName', 'Order Channel Name')}
                      value={formData.name}
                      onChange={handleInputChange('name')}
                      error={!!errors.name}
                      helperText={errors.name}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('procurementOrderChannelId', 'Order Channel ID')}
                      type="number"
                      value={formData.orderTypeId !== undefined && formData.orderTypeId != null ? formData.orderTypeId.toString() : ''}
                      onChange={handleInputChange('orderTypeId')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('code')}
                      value={formData.code ?? ''}
                      onChange={handleInputChange('code')}
                      error={!!errors.code}
                      helperText={errors.code}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('minimumOrderValue')}
                      type="number"
                      value={formData.minimumOrderValue !== undefined && formData.minimumOrderValue != null ? formData.minimumOrderValue.toString() : ''}
                      onChange={handleInputChange('minimumOrderValue')}
                      error={!!errors.minimumOrderValue}
                      helperText={errors.minimumOrderValue || t('minimumOrderValueHelp')}
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <Autocomplete
                      options={instances}
                      getOptionLabel={(option) => option.name}
                      value={instances.find(instance => instance.id === formData.companyId) || null}
                      onChange={handleInstanceChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('instance')}
                          error={!!errors.companyId}
                          helperText={errors.companyId}
                          required
                        />
                      )}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <Autocomplete
                      options={markets}
                      getOptionLabel={(option) => option.name}
                      value={markets.find(market => market.customerTypeId === formData.customerTypeId) || null}
                      onChange={handleMarketChange}
                      disabled={!formData.companyId}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('market')}
                          helperText={t('marketOptional')}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <Stack
                      direction="row"
                      spacing={4}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formData.internal}
                            onChange={handleCheckboxChange('internal')}
                          />
                        }
                        label={t('internal')}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formData.external}
                            onChange={handleCheckboxChange('external')}
                          />
                        }
                        label={t('external')}
                      />
                    </Stack>
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}
                    sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!formData.orderingOnly}
                          onChange={handleCheckboxChange('orderingOnly')}
                        />
                      }
                      label={t('orderingOnly')}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue}
                index={1}>
                <Grid container
                  spacing={4}>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('orderCutoffTime')}
                      type="time"
                      value={convertISODurationToTimeInput(formData?.cutoff?.orderCutOffTime || '')}
                      onChange={handleCutoffChange('orderCutOffTime')}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData?.cutoff?.canPushOrderInEnteredDOS || false}
                          onChange={handleCutoffChange('canPushOrderInEnteredDOS')}
                        />
                      }
                      label={t('canPushOrderInEnteredDOS')}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('supplierName')}
                      value={formData?.cutoff?.supplierName || ''}
                      onChange={handleCutoffChange('supplierName')}
                      error={!!errors.supplierName}
                      helperText={errors.supplierName}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('supplierEmailAddress')}
                      type="email"
                      value={formData?.cutoff?.supplierEmailAddress || ''}
                      onChange={handleCutoffChange('supplierEmailAddress')}
                      error={!!errors.supplierEmailAddress}
                      helperText={errors.supplierEmailAddress}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('supplierEmailAddress2')}
                      type="email"
                      value={formData?.cutoff?.supplierEmailAddress2 || ''}
                      onChange={handleCutoffChange('supplierEmailAddress2')}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              {isDemoMode && (
                <TabPanel value={tabValue}
                  index={2}>
                  <Typography variant="textSM"
                    color="text.secondary"
                    sx={{ mb: 4 }}>
                    {t(
                      'routingIntro',
                      'How orders placed through this channel are routed to the supplier.',
                    )}
                  </Typography>
                  <Grid container
                    spacing={4}>
                    <Grid item
                      xs={12}
                      md={6}>
                      <TextField
                        select
                        label={t('routingStrategy', 'Routing strategy')}
                        value={routingStrategy}
                        onChange={(e) => setRoutingStrategy(e.target.value as OrderRoutingStrategy)}
                        fullWidth
                      >
                        <MenuItem value={OrderRoutingStrategy.OdsSevona}>{t('routingOdsSevona', 'ODS (via Sevona)')}</MenuItem>
                        <MenuItem value={OrderRoutingStrategy.IntegratedSupplier}>{t('routingIntegratedSupplier', 'Approved integrated supplier')}</MenuItem>
                        <MenuItem value={OrderRoutingStrategy.NonIntegratedCsv}>{t('routingNonIntegratedCsv', 'Non-integrated supplier (CSV export)')}</MenuItem>
                        <MenuItem value={OrderRoutingStrategy.PendingApprovalHold}>{t('routingPendingApprovalHold', 'Requires approval before sending (hold)')}</MenuItem>
                      </TextField>
                    </Grid>
                    {routingStrategy === OrderRoutingStrategy.NonIntegratedCsv && (
                      <Grid item
                        xs={12}
                        md={6}>
                        <TextField
                          label={t('routingCsvExportEmail', 'CSV export recipient email')}
                          type="email"
                          value={csvExportEmail}
                          onChange={(e) => setCsvExportEmail(e.target.value)}
                          fullWidth
                        />
                      </Grid>
                    )}
                    {routingStrategy === OrderRoutingStrategy.PendingApprovalHold && (
                      <>
                        <Grid item
                          xs={12}
                          md={6}>
                          <Stack direction="row"
                            alignItems="center"
                            spacing={2}>
                            <Chip label={t('routingHoldStatus', 'Orders held pending approval')}
                              color="warning"
                              size="small" />
                          </Stack>
                        </Grid>
                        <Grid item
                          xs={12}>
                          <TextField
                            label={t('routingApprovalHoldReason', 'Reason orders are held')}
                            value={approvalHoldReason}
                            onChange={(e) => setApprovalHoldReason(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </TabPanel>
              )}
            </Box>
          </Section>
        </Box>
      </Stack>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default OrderChannelForm;
